import React from "react";
import Select from "react-select";

export interface HeadOption {
    value: string;
    label: string;
}

interface GeneratePayrollFormProps {
    // Wizard step currently shown: 1 = Employee, 2 = Earnings & Deductions.
    // Step 3 ("Review") renders the generated payroll result and is handled by
    // the parent. Navigation/actions live in the parent shell footer.
    step: 1 | 2;
    employeeName: string;
    designation: string;
    fromDate: string;
    toDate: string;
    // Latest selectable date (yyyy-mm-dd) for the DAILY date pickers.
    maxDate: string;
    // Calculation mode + MONTHLY-only fields.
    calculationMode: "MONTHLY" | "DAILY";
    assessmentYear: string;
    month: string;
    assessmentYearOptions: HeadOption[];
    monthOptions: HeadOption[];
    onCalculationModeChange: (mode: "MONTHLY" | "DAILY") => void;
    onAssessmentYearChange: (value: string) => void;
    onMonthChange: (value: string) => void;
    incomeHead: string;
    incomeAmount: string;
    deductionHead: string;
    deductionAmount: string;
    reimbursement: string;
    leaves: string;
    isGenerating: boolean;
    formErrors: Record<string, string>;
    incomeHeadSelectOptions: HeadOption[];
    deductionHeadSelectOptions: HeadOption[];
    onEmployeeNameChange: (value: string) => void;
    onDesignationChange: (value: string) => void;
    onFromDateChange: (value: string) => void;
    onToDateChange: (value: string) => void;
    onIncomeHeadChange: (value: string) => void;
    onIncomeAmountChange: (value: string) => void;
    onDeductionHeadChange: (value: string) => void;
    onDeductionAmountChange: (value: string) => void;
    onReimbursementChange: (value: string) => void;
    onLeavesChange: (value: string) => void;
    onGenerate: () => void;
    onPrint: () => void;
    onSave: () => void;
    onDecreaseIncome: () => void;
    onDecreaseDeduction: () => void;
    getSalaryHeadSelectStyles: (hasError?: boolean) => Record<string, unknown>;
    renderFieldError: (field: string) => React.ReactNode;
}

const inputBase =
    "h-10 w-full rounded border bg-white px-3 py-2 text-sm leading-tight dark:bg-slate-800 dark:text-slate-100 transition-colors";
const inputNormal = `${inputBase} border-slate-300 dark:border-slate-600`;
const inputError = `${inputBase} border-red-500`;

const labelCls = "mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide";

export const sanitizeText = (value: string) =>
    value.replace(/[^a-zA-Z0-9\s,.]/g, "");

const sectionCls =
    "rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 shadow-sm";
const sectionHeadCls =
    "flex items-center gap-2 border-b border-slate-100 dark:border-slate-700/70 px-4 sm:px-5 py-3";
const sectionTitleCls = "text-sm font-bold text-slate-800 dark:text-white";
const sectionBodyCls = "p-4 sm:p-5";

const GeneratePayrollForm: React.FC<GeneratePayrollFormProps> = ({
    step,
    employeeName,
    designation,
    fromDate,
    toDate,
    maxDate,
    calculationMode,
    assessmentYear,
    month,
    assessmentYearOptions,
    monthOptions,
    onCalculationModeChange,
    onAssessmentYearChange,
    onMonthChange,
    incomeHead,
    incomeAmount,
    deductionHead,
    deductionAmount,
    reimbursement,
    leaves,
    formErrors,
    incomeHeadSelectOptions,
    deductionHeadSelectOptions,
    onEmployeeNameChange,
    onDesignationChange,
    onFromDateChange,
    onToDateChange,
    onIncomeHeadChange,
    onIncomeAmountChange,
    onDeductionHeadChange,
    onDeductionAmountChange,
    onReimbursementChange,
    onLeavesChange,
    getSalaryHeadSelectStyles,
    renderFieldError,
}) => {
    // State to track if the user is actively typing so the field doesn't lock on the first keystroke
    const [isTypingEmployee, setIsTypingEmployee] = React.useState(false);
    const [isTypingDesignation, setIsTypingDesignation] = React.useState(false);

    // Reset typing state if the parent component clears the values
    React.useEffect(() => {
        if (!employeeName.trim()) setIsTypingEmployee(false);
    }, [employeeName]);

    React.useEffect(() => {
        if (!designation.trim()) setIsTypingDesignation(false);
    }, [designation]);

    // Lock ONLY if there's a prefilled value AND the user isn't currently manually typing it
    const isEmployeeNameLocked = Boolean(employeeName.trim()) && !isTypingEmployee;
    const isDesignationLocked = Boolean(designation.trim()) && !isTypingDesignation;
    const lockedCls = "cursor-not-allowed opacity-70";

    return (
        <div className="space-y-5">
            {/* ══════════ STEP 1 — EMPLOYEE ══════════ */}
            {step === 1 && (
                <div className={sectionCls}>
                    <div className={sectionHeadCls}>
                        <span className="h-3.5 w-1 rounded-full bg-blue-600" aria-hidden />
                        <h3 className={sectionTitleCls}>Employee & Period</h3>
                    </div>
                    <div className={`${sectionBodyCls} space-y-4`}>
                        {/* Calculation mode tabs */}
                        <div>
                            <label className={labelCls}>Calculation Mode</label>
                            <div className="inline-flex rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 p-0.5">
                                {(["MONTHLY", "DAILY"] as const).map((mode) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => onCalculationModeChange(mode)}
                                        className={`px-6 py-1.5 text-sm font-semibold rounded-md transition-colors ${calculationMode === mode
                                            ? "bg-blue-700 text-white shadow-sm"
                                            : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                                            }`}
                                    >
                                        {mode === "MONTHLY" ? "Monthly" : "Daily"}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Employee Name / Designation */}
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                                <label className={labelCls}>
                                    Employee Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={employeeName}
                                    onChange={(e) => {
                                        setIsTypingEmployee(true);
                                        onEmployeeNameChange(sanitizeText(e.target.value));
                                    }}
                                    placeholder="Employee Name"
                                    className={`${formErrors.employeeName ? inputError : inputNormal} ${isEmployeeNameLocked ? lockedCls : ""}`}
                                    disabled={isEmployeeNameLocked}
                                />
                                {renderFieldError("employeeName")}
                            </div>
                            <div>
                                <label className={labelCls}>
                                    Designation <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={designation}
                                    onChange={(e) => {
                                        setIsTypingDesignation(true);
                                        onDesignationChange(sanitizeText(e.target.value));
                                    }}
                                    placeholder="Designation"
                                    className={`${formErrors.designation ? inputError : inputNormal} ${isDesignationLocked ? lockedCls : ""}`}
                                    disabled={isDesignationLocked}
                                />
                                {renderFieldError("designation")}
                            </div>
                        </div>

                        {/* Period — MONTHLY (year + month) or DAILY (from/to date) */}
                        {calculationMode === "MONTHLY" ? (
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div className="flex flex-col min-w-0">
                                    <label className={labelCls}>Assessment Year <span className="text-red-500">*</span></label>
                                    <Select<HeadOption, false>
                                        options={assessmentYearOptions}
                                        value={assessmentYearOptions.find((o) => o.value === assessmentYear) || null}
                                        onChange={(opt) => onAssessmentYearChange(opt?.value || "")}
                                        placeholder="Select Assessment Year"
                                        isSearchable
                                        isClearable={false}
                                        className="w-full"
                                        classNamePrefix="salary-head-select"
                                        menuPlacement="bottom"
                                        menuPosition="fixed"
                                        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                                        noOptionsMessage={() => "No data available"}
                                        styles={getSalaryHeadSelectStyles(Boolean(formErrors.assessmentYear))}
                                    />
                                    {renderFieldError("assessmentYear")}
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <label className={labelCls}>Month <span className="text-red-500">*</span></label>
                                    <Select<HeadOption, false>
                                        options={monthOptions}
                                        value={monthOptions.find((o) => o.value === month) || null}
                                        onChange={(opt) => onMonthChange(opt?.value || "")}
                                        placeholder="Select Month"
                                        isSearchable
                                        isClearable={false}
                                        className="w-full"
                                        classNamePrefix="salary-head-select"
                                        menuPlacement="bottom"
                                        menuPosition="fixed"
                                        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                                        noOptionsMessage={() => "No data available"}
                                        styles={getSalaryHeadSelectStyles(Boolean(formErrors.month))}
                                    />
                                    {renderFieldError("month")}
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div>
                                    <label className={labelCls}>From Date <span className="text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => onFromDateChange(e.target.value)}
                                        max={toDate || maxDate}
                                        className={formErrors.fromDate ? inputError : inputNormal}
                                    />
                                    {renderFieldError("fromDate")}
                                </div>
                                <div>
                                    <label className={labelCls}>To Date <span className="text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => onToDateChange(e.target.value)}
                                        min={fromDate || undefined}
                                        max={maxDate}
                                        className={formErrors.toDate ? inputError : inputNormal}
                                    />
                                    {renderFieldError("toDate")}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══════════ STEP 2 — EARNINGS & DEDUCTIONS ══════════ */}
            {step === 2 && (
                <>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {/* Income */}
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 shadow-sm transition-colors">
                            <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] items-center bg-gradient-to-r from-[#0b3b8c] via-[#0f4fa8] to-[#1d77d0] px-3 py-2 text-xs font-semibold text-white">
                                <span>Income Head</span>
                                <span>Amount</span>
                            </div>
                            <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] items-start gap-2 bg-white px-3 py-3 dark:bg-slate-800 transition-colors">
                                <div className="flex flex-col min-w-0">
                                    <Select<HeadOption, false>
                                        options={incomeHeadSelectOptions}
                                        value={incomeHeadSelectOptions.find((o) => o.value === incomeHead) || null}
                                        onChange={(opt) => onIncomeHeadChange(opt?.value || "")}
                                        placeholder="Select Income Head"
                                        isSearchable
                                        isClearable={false}
                                        className="w-full"
                                        classNamePrefix="salary-head-select"
                                        menuPlacement="bottom"
                                        menuPosition="fixed"
                                        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                                        noOptionsMessage={() => "No data available"}
                                        styles={getSalaryHeadSelectStyles(Boolean(formErrors.incomeHead))}
                                    />
                                    {renderFieldError("incomeHead")}
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <input
                                        type="text"
                                        value={incomeAmount}
                                        onChange={(e) => onIncomeAmountChange(e.target.value)}
                                        placeholder="Rs."
                                        className={`${formErrors.incomeAmount ? inputError : inputNormal}`}
                                    />
                                    {renderFieldError("incomeAmount")}
                                </div>
                            </div>
                        </div>

                        {/* Deduction */}
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 shadow-sm transition-colors">
                            <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] items-center bg-gradient-to-r from-[#0b3b8c] via-[#0f4fa8] to-[#1d77d0] px-3 py-2 text-xs font-semibold text-white">
                                <span>Deduction Head</span>
                                <span>Amount</span>
                            </div>
                            <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] items-start gap-2 bg-white px-3 py-3 dark:bg-slate-800 transition-colors">
                                <div className="flex flex-col min-w-0">
                                    <Select<HeadOption, false>
                                        options={deductionHeadSelectOptions}
                                        value={deductionHeadSelectOptions.find((o) => o.value === deductionHead) || null}
                                        onChange={(opt) => onDeductionHeadChange(opt?.value || "")}
                                        placeholder="Select Deduction Head"
                                        isSearchable
                                        isClearable={false}
                                        className="w-full"
                                        classNamePrefix="salary-head-select"
                                        menuPlacement="bottom"
                                        menuPosition="fixed"
                                        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                                        noOptionsMessage={() => "No data available"}
                                        styles={getSalaryHeadSelectStyles()}
                                    />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <input
                                        type="text"
                                        value={deductionAmount}
                                        onChange={(e) => onDeductionAmountChange(e.target.value)}
                                        placeholder="Rs."
                                        className={`${inputNormal}`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Reimbursement / Leaves */}
                    <div className={sectionCls}>
                        <div className={sectionHeadCls}>
                            <span className="h-3.5 w-1 rounded-full bg-blue-600" aria-hidden />
                            <h3 className={sectionTitleCls}>Adjustments</h3>
                        </div>
                        <div className={sectionBodyCls}>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <label className={labelCls}>Reimbursement</label>
                                    <input
                                        type="number"
                                        value={reimbursement}
                                        onChange={(e) => onReimbursementChange(e.target.value)}
                                        onBlur={(e) => {
                                            if (e.target.value.trim() === "") {
                                                onReimbursementChange("0");
                                            }
                                        }}
                                        className={inputNormal}
                                    />
                                </div>
                                <div>
                                    <label className={labelCls}>Leaves</label>
                                    <input
                                        type="number"
                                        value={leaves}
                                        onChange={(e) => onLeavesChange(e.target.value)}
                                        onBlur={(e) => {
                                            if (e.target.value.trim() === "") {
                                                onLeavesChange("0");
                                            }
                                        }}
                                        className={inputNormal}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default GeneratePayrollForm;
